// AnalyticsDashboard.js - Version: v1 i23 (FINAL & KOMPLETT: Inkl. Funnel, LP- & Button-Performance)
import React, { useState, useMemo, useEffect } from 'react';
import BackButton from "./BackButton";

import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import "./AnalyticsDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Initialer Zustand für das Notizformular
const initialNewNoteState = {
    change_date: new Date().toISOString().slice(0, 16),
    ad_group: '',
    reason_for_change: '',
    location_of_change: '',
    description_of_change: ''
};

// Hilfsfunktionen für Diagrammfarben
const genChartColors = (numColors) => {
    const baseColors = [
        'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)', 'rgba(255, 206, 86, 0.7)',
        'rgba(75, 192, 192, 0.7)', 'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
        'rgba(199, 199, 199, 0.7)', 'rgba(83, 102, 83, 0.7)', 'rgba(140, 150, 198, 0.7)',
        'rgba(210, 130, 100, 0.7)', 'rgba(255, 138, 128, 0.7)', 'rgba(128, 222, 234, 0.7)'
    ];
    const colors = [];
    for (let i = 0; i < numColors; i++) {
        colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
};
const genBorderColors = (colors) => colors.map(color => color.replace('0.7', '1'));

export default function AnalyticsDashboard() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [startDate, setStartDate] = useState(sevenDaysAgo);
    const [endDate, setEndDate] = useState(today);
    
    const [keywordPerformanceData, setKeywordPerformanceData] = useState(null);
    const [overallStats, setOverallStats] = useState({
        totalUniqueUsers: 0,
        avgOverallDuration: "N/A",
        avgOverallDurationSeconds: null
    });
    const [ctaTypeDistributionData, setCtaTypeDistributionData] = useState(null);
    const [dailyUserStatsData, setDailyUserStatsData] = useState(null);
    
    const [urlUtmSourceDistribution, setUrlUtmSourceDistribution] = useState(null);
    const [urlUtmMediumDistribution, setUrlUtmMediumDistribution] = useState(null);
    const [urlUtmCampaignDistribution, setUrlUtmCampaignDistribution] = useState(null);

    const [selectedKeyword, setSelectedKeyword] = useState(null);
    const [keywordUserDetails, setKeywordUserDetails] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const [isLoading, setIsLoading] = useState(false); 
    const [message, setMessage] = useState("Bitte Zeitraum für Analyse auswählen und Analyse starten.");
    const token = localStorage.getItem("token");

    const [keywordSortConfig, setKeywordSortConfig] = useState({ key: 'uniqueUsers', direction: 'descending' });
    const [userDetailSortConfig, setUserDetailSortConfig] = useState({ key: 'visitorId', direction: 'ascending' });
    const [lpSortConfig, setLpSortConfig] = useState({ key: 'totalVisitors', direction: 'descending' });
    const [buttonSortConfig, setButtonSortConfig] = useState({ key: 'click_count', direction: 'descending' });

    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState({...initialNewNoteState}); 
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [noteMessage, setNoteMessage] = useState("");

    const [funnelData, setFunnelData] = useState(null);
    const [isFunnelLoading, setIsFunnelLoading] = useState(false);
    const [selectedFunnelLP, setSelectedFunnelLP] = useState('');
    const [funnelMessage, setFunnelMessage] = useState("Hier klicken, um Funnel-Daten zu laden.");

    const [lpPerformanceData, setLpPerformanceData] = useState(null);
    const [isLpLoading, setIsLpLoading] = useState(false);
    const [lpPerformanceMessage, setLpPerformanceMessage] = useState("Hier klicken, um LP-Performance-Daten zu laden.");
    
    const [buttonPerformanceData, setButtonPerformanceData] = useState(null);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [buttonPerformanceMessage, setButtonPerformanceMessage] = useState("Hier klicken, um Button-Performance zu laden.");

    const fetchMainAnalyticsData = async (start, end) => {
        if (!token) {
            setMessage("❌ Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }
        
        setIsLoading(true);
        setMessage("Lade Analysedaten...");
        setKeywordPerformanceData(null); 
        setOverallStats({ totalUniqueUsers: 0, avgOverallDuration: "N/A", avgOverallDurationSeconds: null }); 
        setCtaTypeDistributionData(null);
        setDailyUserStatsData(null);
        setUrlUtmSourceDistribution(null);
        setUrlUtmMediumDistribution(null);
        setUrlUtmCampaignDistribution(null);

        const formattedStartDate = start.toISOString().split('T')[0];
        const formattedEndDate = end.toISOString().split('T')[0];

        try {
            const response = await fetch(`https://freiseinapp.de/api/get_analytics_data.php?startDate=${formattedStartDate}&endDate=${formattedEndDate}`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            });
            setIsLoading(false);
            if (!response.ok) {
                const errData = await response.json().catch(() => ({ message: 'Serverantwort nicht lesbar oder kein JSON.' }));
                throw new Error(`HTTP Fehler! Status: ${response.status} - ${errData.message || 'Unbekannter Serverfehler'}`);
            }
            const data = await response.json();
            if (data.success && data.analytics) {
                setKeywordPerformanceData(data.analytics.keywordData || []); 
                setOverallStats(data.analytics.overallStats || { totalUniqueUsers: 0, avgOverallDuration: "N/A", avgOverallDurationSeconds: null });
                setCtaTypeDistributionData(data.analytics.ctaTypeDistribution || []);
                setDailyUserStatsData(data.analytics.dailyUserStats || []);
                
                setUrlUtmSourceDistribution(data.analytics.urlUtmSourceDistribution || []);
                setUrlUtmMediumDistribution(data.analytics.urlUtmMediumDistribution || []);
                setUrlUtmCampaignDistribution(data.analytics.urlUtmCampaignDistribution || []);

                const dataExists = (data.analytics.keywordData && data.analytics.keywordData.length > 0) || 
                                   (data.analytics.ctaTypeDistribution && data.analytics.ctaTypeDistribution.length > 0) ||
                                   (data.analytics.dailyUserStats && data.analytics.dailyUserStats.length > 0) ||
                                   (data.analytics.urlUtmSourceDistribution && data.analytics.urlUtmSourceDistribution.length > 0) ||
                                   (data.analytics.urlUtmMediumDistribution && data.analytics.urlUtmMediumDistribution.length > 0) ||
                                   (data.analytics.urlUtmCampaignDistribution && data.analytics.urlUtmCampaignDistribution.length > 0);
                setMessage(dataExists ? (data.message || "Daten erfolgreich geladen.") : "📊 Keine Analysedaten für den ausgewählten Zeitraum gefunden.");
            } else {
                setMessage(data.message || "❌ Fehler beim Verarbeiten der Analysedaten vom Backend.");
                setKeywordPerformanceData(null); setCtaTypeDistributionData(null); setDailyUserStatsData(null);
                setUrlUtmSourceDistribution(null); setUrlUtmMediumDistribution(null); setUrlUtmCampaignDistribution(null);
            }
        } catch (error) {
            console.error("❌ Fehler beim Laden der Haupt-Analysedaten:", error);
            setMessage(`❌ Fehler (Hauptdaten): ${error.message}`);
            setKeywordPerformanceData(null); setCtaTypeDistributionData(null); setDailyUserStatsData(null);
            setUrlUtmSourceDistribution(null); setUrlUtmMediumDistribution(null); setUrlUtmCampaignDistribution(null);
            setIsLoading(false);
        }
    };

    const fetchKeywordUserDetails = async (keyword, start, end) => {
        if (!token || !keyword) return;
        setIsDetailLoading(true);
        setMessage(`Lade User-Details für Keyword: "${keyword}"...`);
        setKeywordUserDetails(null);
        const formattedStartDate = start.toISOString().split('T')[0];
        const formattedEndDate = end.toISOString().split('T')[0];

        try {
            const response = await fetch(`https://freiseinapp.de/api/get_keyword_user_details.php?startDate=${formattedStartDate}&endDate=${formattedEndDate}&keyword=${encodeURIComponent(keyword)}`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            });
            setIsDetailLoading(false);
            if (!response.ok) {
                const errData = await response.json().catch(() => ({ message: 'Serverantwort nicht lesbar oder kein JSON.' }));
                throw new Error(`HTTP Fehler! Status: ${response.status} - ${errData.message || 'Unbekannter Serverfehler bei User-Details'}`);
            }
            const data = await response.json();
            if (data.success && data.keywordUserDetailData) {
                setKeywordUserDetails(data.keywordUserDetailData);
                setMessage(data.keywordUserDetailData.length > 0 ? `User-Details für "${keyword}" geladen.` : `📊 Keine User-Details für "${keyword}" gefunden.`);
            } else {
                setMessage(data.message || `❌ Fehler beim Laden der User-Details für "${keyword}".`);
                setKeywordUserDetails(null);
            }
        } catch (error) {
            console.error(`❌ Fehler beim Laden der User-Details für "${keyword}":`, error);
            setMessage(`❌ Fehler (User-Details): ${error.message}`);
            setKeywordUserDetails(null);
            setIsDetailLoading(false);
        }
    };

    const fetchNotes = async () => {
        if (!token) {
            setNoteMessage("❌ Token für Notizen fehlt.");
            return;
        }
        setIsLoadingNotes(true);
        try {
            const response = await fetch(`https://freiseinapp.de/api/handle_notes.php`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success) {
                setNotes(data.notes || []);
            } else {
                setNoteMessage(data.message || "❌ Fehler beim Laden der Notizen.");
                console.error("Fehler beim Laden der Notizen vom Backend:", data.message);
            }
        } catch (error) {
            console.error("Netzwerkfehler beim Laden der Notizen:", error);
            setNoteMessage("❌ Netzwerkfehler beim Laden der Notizen.");
        }
        setIsLoadingNotes(false);
    };

    const fetchFunnelData = async () => {
        if (!token) {
            setFunnelMessage("❌ Authentifizierungstoken fehlt.");
            return;
        }
        setIsFunnelLoading(true);
        setFunnelMessage("Lade Funnel-Daten...");
        setFunnelData(null);
        setSelectedFunnelLP('');
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        try {
            const response = await fetch(`https://freiseinapp.de/api/get_analytics_data.php?startDate=${formattedStartDate}&endDate=${formattedEndDate}&analysisType=funnel`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}` },
            });
            setIsFunnelLoading(false);
            if (!response.ok) throw new Error(`HTTP Fehler! Status: ${response.status}`);
            const data = await response.json();
            if (data.success && data.funnelData) {
                const hasData = Object.keys(data.funnelData).length > 0;
                setFunnelData(data.funnelData);
                setFunnelMessage(hasData ? "Daten geladen. Bitte eine Landingpage auswählen." : "📊 Keine Funnel-Daten für diesen Zeitraum gefunden.");
            } else {
                setFunnelMessage(data.message || "❌ Fehler beim Laden der Funnel-Daten.");
            }
        } catch (error) {
            console.error("❌ Fehler beim Laden der Funnel-Daten:", error);
            setFunnelMessage(`❌ Fehler: ${error.message}`);
            setIsFunnelLoading(false);
        }
    };
    
    const fetchLpPerformanceData = async () => {
        if (!token) {
            setLpPerformanceMessage("❌ Authentifizierungstoken fehlt.");
            return;
        }
        setIsLpLoading(true);
        setLpPerformanceMessage("Lade LP-Performance-Daten...");
        setLpPerformanceData(null);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        try {
            const response = await fetch(`https://freiseinapp.de/api/get_analytics_data.php?startDate=${formattedStartDate}&endDate=${formattedEndDate}&analysisType=lp_performance`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}` },
            });
            setIsLpLoading(false);
            if (!response.ok) throw new Error(`HTTP Fehler! Status: ${response.status}`);
            const data = await response.json();
            if (data.success && data.lpPerformanceData) {
                const hasData = data.lpPerformanceData.length > 0;
                setLpPerformanceData(data.lpPerformanceData);
                setLpPerformanceMessage(hasData ? "Daten geladen." : "📊 Keine LP-Performance-Daten für diesen Zeitraum gefunden.");
            } else {
                setLpPerformanceMessage(data.message || "❌ Fehler beim Laden der LP-Performance-Daten.");
            }
        } catch (error) {
            console.error("❌ Fehler beim Laden der LP-Performance-Daten:", error);
            setLpPerformanceMessage(`❌ Fehler: ${error.message}`);
            setIsLpLoading(false);
        }
    };
    
    const fetchButtonPerformanceData = async () => {
        if (!token) {
            setButtonPerformanceMessage("❌ Authentifizierungstoken fehlt.");
            return;
        }
        setIsButtonLoading(true);
        setButtonPerformanceMessage("Lade Button-Performance...");
        setButtonPerformanceData(null);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        try {
            const response = await fetch(`https://freiseinapp.de/api/get_analytics_data.php?startDate=${formattedStartDate}&endDate=${formattedEndDate}&analysisType=button_performance`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${token}` },
            });
            setIsButtonLoading(false);
            if (!response.ok) throw new Error(`HTTP Fehler! Status: ${response.status}`);
            const data = await response.json();
            if (data.success && data.buttonPerformanceData) {
                const hasData = data.buttonPerformanceData.length > 0;
                setButtonPerformanceData(data.buttonPerformanceData);
                setButtonPerformanceMessage(hasData ? "Daten geladen." : "📊 Keine Button-Performance-Daten für diesen Zeitraum gefunden.");
            } else {
                setButtonPerformanceMessage(data.message || "❌ Fehler beim Laden der Button-Performance-Daten.");
            }
        } catch (error) {
            console.error("❌ Fehler beim Laden der Button-Performance-Daten:", error);
            setButtonPerformanceMessage(`❌ Fehler: ${error.message}`);
            setIsButtonLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, [token]);

    const handleFetchDataClick = () => {
        if (endDate < startDate) {
            setMessage("⚠️ Das Enddatum darf nicht vor dem Startdatum liegen.");
            return;
        }
        setKeywordSortConfig({ key: 'uniqueUsers', direction: 'descending' });
        setSelectedKeyword(null); 
        setKeywordUserDetails(null);
        fetchMainAnalyticsData(startDate, endDate);
    };

    const handleKeywordRowClick = (keyword) => {
        setSelectedKeyword(keyword);
        setUserDetailSortConfig({ key: 'visitorId', direction: 'ascending' });
        fetchKeywordUserDetails(keyword, startDate, endDate);
    };

    const handleBackToOverviewClick = () => {
        setSelectedKeyword(null);
        setKeywordUserDetails(null);
        setMessage("Zurück zur Keyword-Übersicht."); 
    };

    const handleNewNoteChange = (e) => {
        const { name, value } = e.target;
        setNewNote(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveNote = async (e) => {
        e.preventDefault();
        if (!token) {
            setNoteMessage("❌ Token zum Speichern der Notiz fehlt.");
            return;
        }
        if (!newNote.change_date || !newNote.description_of_change.trim()) {
            setNoteMessage("⚠️ Bitte Datum/Uhrzeit und eine Beschreibung für die Notiz angeben.");
            return;
        }
        setIsLoadingNotes(true);
        setNoteMessage("Speichere Notiz...");
        try {
            let formattedChangeDate = newNote.change_date;
            if (formattedChangeDate.includes('T')) {
                 formattedChangeDate = formattedChangeDate.replace('T', ' ');
            }
            if (formattedChangeDate.length === 16) { 
                formattedChangeDate += ':00';
            }
            
            const noteToSave = { ...newNote, change_date: formattedChangeDate };

            const response = await fetch(`https://freiseinapp.de/api/handle_notes.php`, {
                method: "POST",
                headers: { 
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(noteToSave),
            });
            const data = await response.json();
            if (data.success) {
                setNoteMessage("✅ Notiz erfolgreich gespeichert!");
                setNewNote({...initialNewNoteState}); 
                fetchNotes(); 
            } else {
                setNoteMessage(data.message || "❌ Fehler beim Speichern der Notiz.");
                console.error("Fehler beim Speichern der Notiz vom Backend:", data.message);
            }
        } catch (error) {
            console.error("Netzwerkfehler beim Speichern der Notiz:", error);
            setNoteMessage("❌ Netzwerkfehler beim Speichern der Notiz.");
        }
        setIsLoadingNotes(false);
        setTimeout(() => setNoteMessage(""), 4000); 
    };

    const sortedKeywordPerformanceData = useMemo(() => {
        if (!keywordPerformanceData) return null;
        let sortableItems = [...keywordPerformanceData];
        if (keywordSortConfig.key !== null) {
            sortableItems.sort((a, b) => { 
                let aValue = a[keywordSortConfig.key];
                let bValue = b[keywordSortConfig.key];
                if (keywordSortConfig.key === 'avgDuration') {
                    aValue = a.avgDurationSeconds !== null ? a.avgDurationSeconds : (keywordSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                    bValue = b.avgDurationSeconds !== null ? b.avgDurationSeconds : (keywordSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                } else if (typeof aValue === 'string' && typeof bValue === 'string' && keywordSortConfig.key === 'keyword') {
                    aValue = aValue.toLowerCase(); bValue = bValue.toLowerCase();
                }
                if (aValue < bValue) return keywordSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return keywordSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [keywordPerformanceData, keywordSortConfig]);

    const sortedKeywordUserDetails = useMemo(() => {
        if (!keywordUserDetails) return null;
        let sortableItems = [...keywordUserDetails];
        if (userDetailSortConfig.key !== null) {
            sortableItems.sort((a, b) => { 
                let aValue = a[userDetailSortConfig.key];
                let bValue = b[userDetailSortConfig.key];
                if (userDetailSortConfig.key === 'totalDurationFormatted') {
                    aValue = a.totalDurationSeconds !== null ? a.totalDurationSeconds : (userDetailSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                    bValue = b.totalDurationSeconds !== null ? b.totalDurationSeconds : (userDetailSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                } else if (userDetailSortConfig.key === 'avgDurationPerSessionFormatted') {
                    aValue = a.avgDurationPerSessionSeconds !== null ? a.avgDurationPerSessionSeconds : (userDetailSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                    bValue = b.avgDurationPerSessionSeconds !== null ? b.avgDurationPerSessionSeconds : (userDetailSortConfig.direction === 'ascending' ? Infinity : -Infinity);
                } else if (typeof aValue === 'string' && typeof bValue === 'string' && userDetailSortConfig.key === 'visitorId') {
                    aValue = aValue.toLowerCase(); bValue = bValue.toLowerCase();
                }
                if (aValue < bValue) return userDetailSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return userDetailSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [keywordUserDetails, userDetailSortConfig]);
    
    const sortedLpPerformanceData = useMemo(() => {
        if (!lpPerformanceData) return null;
        let sortableItems = [...lpPerformanceData];
        if (lpSortConfig.key !== null) {
            sortableItems.sort((a, b) => { 
                let aValue = a[lpSortConfig.key];
                let bValue = b[lpSortConfig.key];
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase(); bValue = bValue.toLowerCase();
                }
                if (aValue < bValue) return lpSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return lpSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [lpPerformanceData, lpSortConfig]);
    
    const sortedButtonPerformanceData = useMemo(() => {
        if (!buttonPerformanceData) return null;
        let sortableItems = [...buttonPerformanceData];
        if (buttonSortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[buttonSortConfig.key];
                let bValue = b[buttonSortConfig.key];
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = (aValue || "").toLowerCase();
                    bValue = (bValue || "").toLowerCase();
                }
                if (aValue < bValue) return buttonSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return buttonSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [buttonPerformanceData, buttonSortConfig]);

    const requestSort = (tableType, key) => { 
        let currentConfig, setConfig;
        switch (tableType) {
            case 'keyword': currentConfig = keywordSortConfig; setConfig = setKeywordSortConfig; break;
            case 'userDetail': currentConfig = userDetailSortConfig; setConfig = setUserDetailSortConfig; break;
            case 'lpPerformance': currentConfig = lpSortConfig; setConfig = setLpSortConfig; break;
            case 'buttonPerformance': currentConfig = buttonSortConfig; setConfig = setButtonSortConfig; break;
            default: return;
        }
        let direction = 'ascending';
        if (currentConfig.key === key && currentConfig.direction === 'ascending') { direction = 'descending';}
        setConfig({ key, direction });
    };
    const getSortIndicator = (tableType, columnKey) => { 
        let config;
        switch (tableType) {
            case 'keyword': config = keywordSortConfig; break;
            case 'userDetail': config = userDetailSortConfig; break;
            case 'lpPerformance': config = lpSortConfig; break;
            case 'buttonPerformance': config = buttonSortConfig; break;
            default: return '';
        }
        if (config.key === columnKey) { return config.direction === 'ascending' ? ' ▲' : ' ▼'; }
        return '';
    };
    
    const formatDuration = (durationString) => durationString || "N/A";
    const formatNoteDateTimeForDisplay = (dateTimeString) => {
        if (!dateTimeString) return "N/A";
        try {
            const date = new Date(dateTimeString.replace('T', ' ')); 
            return date.toLocaleString('de-DE', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        } catch (e) { return "Ungültiges Datum"; }
    };

    const topKeywordsChartData = useMemo(() => {
        if (!sortedKeywordPerformanceData) return null;
        const topN = 10;
        const dataForChart = [...sortedKeywordPerformanceData]
            .sort((a,b) => (b.uniqueUsers || 0) - (a.uniqueUsers || 0))
            .slice(0, topN);
        return {
            labels: dataForChart.map(d => d.keyword),
            datasets: [{
                label: 'Eindeutige User',
                data: dataForChart.map(d => d.uniqueUsers),
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            }],
        };
    }, [sortedKeywordPerformanceData]);
    
    const keywordChartOptions = { 
        indexAxis: 'y', 
        responsive: true, 
        maintainAspectRatio: false, 
        plugins: { 
            legend: { display: false }, 
            title: { display: true, text: 'Top Keywords nach User-Anzahl' }
        }, 
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    const ctaTypeChartData = useMemo(() => {
        if (!ctaTypeDistributionData || ctaTypeDistributionData.length === 0) return null;
        const backgroundColors = genChartColors(ctaTypeDistributionData.length);
        return {
            labels: ctaTypeDistributionData.map(d => d.cta_type || 'Unbekannt'),
            datasets: [{
                label: 'CTA Klicks',
                data: ctaTypeDistributionData.map(d => d.count),
                backgroundColor: backgroundColors,
                borderColor: genBorderColors(backgroundColors),
                borderWidth: 1,
            }]
        };
    }, [ctaTypeDistributionData]);

    const ctaTypeChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Verteilung der geklickten CTA-Typen' }
        }
    };

    const dailyUserChartData = useMemo(() => {
        if (!dailyUserStatsData || dailyUserStatsData.length === 0) return null;
        const labels = dailyUserStatsData.map(d => {
            const dateParts = d.date.split('-');
            return `${dateParts[2]}.${dateParts[1]}`; 
        });
        return {
            labels: labels,
            datasets: [{
                label: 'Eindeutige User pro Tag',
                data: dailyUserStatsData.map(d => d.users),
                fill: false,
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1
            }]
        };
    }, [dailyUserStatsData]);

    const dailyUserChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: 'Täglicher User-Verlauf' }
        },
        scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } }
        }
    };

    const urlUtmSourceChartPreparedData = useMemo(() => {
        if (!urlUtmSourceDistribution || urlUtmSourceDistribution.length === 0) return null;
        const backgroundColors = genChartColors(urlUtmSourceDistribution.length);
        return {
            labels: urlUtmSourceDistribution.map(d => d.url_utm_source || 'Unbekannt/Nicht Gesetzt'),
            datasets: [{
                label: 'Anzahl Events',
                data: urlUtmSourceDistribution.map(d => d.event_count),
                backgroundColor: backgroundColors,
                borderColor: genBorderColors(backgroundColors),
                borderWidth: 1,
            }]
        };
    }, [urlUtmSourceDistribution]);

    const urlUtmSourceChartOptions = {
        indexAxis: 'y', 
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }, 
            title: { display: true, text: 'Events nach URL UTM Source' }
        },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    const urlUtmMediumChartPreparedData = useMemo(() => {
        if (!urlUtmMediumDistribution || urlUtmMediumDistribution.length === 0) return null;
        const backgroundColors = genChartColors(urlUtmMediumDistribution.length);
        return {
            labels: urlUtmMediumDistribution.map(d => d.url_utm_medium || 'Unbekannt/Nicht Gesetzt'),
            datasets: [{
                label: 'Anzahl Events',
                data: urlUtmMediumDistribution.map(d => d.event_count),
                backgroundColor: backgroundColors,
                borderColor: genBorderColors(backgroundColors),
                borderWidth: 1,
            }]
        };
    }, [urlUtmMediumDistribution]);

    const urlUtmMediumChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right', labels:{boxWidth: 12} },
            title: { display: true, text: 'Events nach URL UTM Medium' }
        }
    };

    const urlUtmCampaignChartPreparedData = useMemo(() => {
        if (!urlUtmCampaignDistribution || urlUtmCampaignDistribution.length === 0) return null;
        const backgroundColors = genChartColors(urlUtmCampaignDistribution.length);
        return {
            labels: urlUtmCampaignDistribution.map(d => d.url_utm_campaign || 'Unbekannt/Nicht Gesetzt'),
            datasets: [{
                label: 'Anzahl Events',
                data: urlUtmCampaignDistribution.map(d => d.event_count),
                backgroundColor: backgroundColors,
                borderColor: genBorderColors(backgroundColors),
                borderWidth: 1,
            }]
        };
    }, [urlUtmCampaignDistribution]);

    const urlUtmCampaignChartOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: 'Events nach URL UTM Campaign' }
        },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    const funnelLPs = useMemo(() => funnelData ? Object.keys(funnelData) : [], [funnelData]);
    const selectedFunnelData = useMemo(() => {
        if (!selectedFunnelLP || !funnelData || !funnelData[selectedFunnelLP]) return null;
        
        const data = funnelData[selectedFunnelLP];
        const maxVisitors = data.length > 0 ? data[0].unique_visitors : 0;

        return data.map((row, index) => {
            const dropOff = index > 0 ? data[index-1].unique_visitors - row.unique_visitors : 0;
            const dropOffPercent = (index > 0 && data[index-1].unique_visitors > 0) ? (dropOff / data[index-1].unique_visitors * 100).toFixed(1) : '0.0';
            const retentionPercent = maxVisitors > 0 ? (row.unique_visitors / maxVisitors * 100).toFixed(1) : '0.0';
            return { ...row, dropOff, dropOffPercent, retentionPercent };
        });

    }, [funnelData, selectedFunnelLP]);

    return (
        <div className="analytics-page-wrapper">
            <div className="analytics-dashboard-container">
                <header className="analytics-header">
                    <h2>📊 Landingpage Analyse</h2>
                    {selectedKeyword && (
                        <button onClick={handleBackToOverviewClick} className="back-to-overview-button">
                            ‹ Zurück zur Keyword-Übersicht
                        </button>
                    )}
                </header>

                {message && !selectedKeyword && 
                    <p className={`message-bar ${message.startsWith('❌') || message.startsWith('⚠️') ? "error" : ((message.startsWith("📊") || message.includes("geladen") || message.includes("Übersicht")) ? "info" : (isLoading ? "loading" : "success"))}`}>
                        {message}
                    </p>
                }
                {noteMessage && !selectedKeyword &&
                    <p className={`message-bar ${noteMessage.startsWith('❌') || noteMessage.startsWith('⚠️') ? "error" : "success"}`}>
                        {noteMessage}
                    </p>
                }
                {message && selectedKeyword && (isLoading || isDetailLoading) &&
                     <p className="message-bar loading">{message}</p>
                }
                 {message && selectedKeyword && !isLoading && !isDetailLoading &&
                     <p className={`message-bar ${message.startsWith('❌') || message.startsWith('⚠️') ? "error" : (message.startsWith("📊") ? "info" : "success")}`}>{message}</p>
                }

                {!selectedKeyword && (
                    <div className="date-filter-section">
                        <div>
                            <label htmlFor="startDateAnalytics">Startdatum:</label>
                            <input 
                                type="date" 
                                id="startDateAnalytics"
                                value={startDate.toISOString().split('T')[0]} 
                                onChange={(e) => setStartDate(new Date(e.target.value))}
                                className="date-input"
                                disabled={isLoading || isDetailLoading}
                            />
                        </div>
                        <div>
                            <label htmlFor="endDateAnalytics">Enddatum:</label>
                             <input 
                                type="date" 
                                id="endDateAnalytics"
                                value={endDate.toISOString().split('T')[0]} 
                                onChange={(e) => setEndDate(new Date(e.target.value))}
                                className="date-input"
                                min={startDate.toISOString().split('T')[0]}
                                disabled={isLoading || isDetailLoading}
                            />
                        </div>
                        <button onClick={handleFetchDataClick} className="analyze-button" disabled={isLoading || isDetailLoading}>
                            {isLoading ? "Lade Übersicht..." : "Analyse starten"}
                        </button>
                    </div>
                )}

                {!selectedKeyword && (
                    <div className="lp-performance-section results-section">
                        <h3>Gesamt-Performance pro Landingpage</h3>
                        <div className="lp-performance-controls">
                            <button onClick={fetchLpPerformanceData} className="analyze-button" disabled={isLpLoading}>
                                {isLpLoading ? "Lade Daten..." : "LP-Performance laden"}
                            </button>
                        </div>
                        
                        {<p className={`message-bar ${isLpLoading ? 'loading' : 'info'}`}>{lpPerformanceMessage}</p>}

                        {sortedLpPerformanceData && (
                             <div className="table-wrapper">
                                <table className="analytics-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('lpPerformance', 'landing_page_url')} className="sortable-header">Landingpage{getSortIndicator('lpPerformance', 'landing_page_url')}</th>
                                            <th onClick={() => requestSort('lpPerformance', 'totalVisitors')} className="sortable-header">Besucher{getSortIndicator('lpPerformance', 'totalVisitors')}</th>
                                            <th onClick={() => requestSort('lpPerformance', 'totalClicks')} className="sortable-header">Klicks (Gesamt){getSortIndicator('lpPerformance', 'totalClicks')}</th>
                                            <th onClick={() => requestSort('lpPerformance', 'totalSectionViews')} className="sortable-header">Sektionen (Gesamt){getSortIndicator('lpPerformance', 'totalSectionViews')}</th>
                                            <th onClick={() => requestSort('lpPerformance', 'conversionRate')} className="sortable-header">Konversionsrate (%){getSortIndicator('lpPerformance', 'conversionRate')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedLpPerformanceData.map(item => (
                                            <tr key={item.landing_page_url}>
                                                <td>{item.landing_page_url.replace(/https?:\/\//, '')}</td>
                                                <td>{item.totalVisitors}</td>
                                                <td>{item.totalClicks}</td>
                                                <td>{item.totalSectionViews}</td>
                                                <td className="conversion-rate-cell">{item.conversionRate.toFixed(2)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                
                {!selectedKeyword && (
                    <div className="button-performance-section results-section">
                        <h3>Button-Performance (CTA-Ranking)</h3>
                        <div className="button-performance-controls">
                            <button onClick={fetchButtonPerformanceData} className="analyze-button" disabled={isButtonLoading}>
                                {isButtonLoading ? "Lade Daten..." : "Button-Performance laden"}
                            </button>
                        </div>
                        {<p className={`message-bar ${isButtonLoading ? 'loading' : 'info'}`}>{buttonPerformanceMessage}</p>}
                        {sortedButtonPerformanceData && (
                             <div className="table-wrapper">
                                <table className="analytics-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('buttonPerformance', 'cta_id')} className="sortable-header">Button ID{getSortIndicator('buttonPerformance', 'cta_id')}</th>
                                            <th onClick={() => requestSort('buttonPerformance', 'cta_text')} className="sortable-header">Button Text{getSortIndicator('buttonPerformance', 'cta_text')}</th>
                                            <th onClick={() => requestSort('buttonPerformance', 'click_count')} className="sortable-header">Anzahl Klicks{getSortIndicator('buttonPerformance', 'click_count')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedButtonPerformanceData.map(item => (
                                            <tr key={item.cta_id || item.cta_text}>
                                                <td>{item.cta_id || '(Keine ID)'}</td>
                                                <td>{item.cta_text || '(Kein Text)'}</td>
                                                <td>{item.click_count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {!selectedKeyword && (
                    <div className="funnel-section results-section">
                        <h3>Funnel-Analyse pro Landingpage</h3>
                        <div className="funnel-controls">
                            <button onClick={fetchFunnelData} className="analyze-button" disabled={isFunnelLoading}>
                                {isFunnelLoading ? "Lade Daten..." : "Funnel-Daten laden"}
                            </button>
                            {funnelData && funnelLPs.length > 0 && (
                                <select 
                                    value={selectedFunnelLP} 
                                    onChange={(e) => setSelectedFunnelLP(e.target.value)}
                                    className="funnel-lp-select"
                                >
                                    <option value="">-- Landingpage auswählen --</option>
                                    {funnelLPs.map(lp => <option key={lp} value={lp}>{lp.replace(/https?:\/\//, '')}</option>)}
                                </select>
                            )}
                        </div>
                        
                        {<p className={`message-bar ${isFunnelLoading ? 'loading' : 'info'}`}>{funnelMessage}</p>}

                        {selectedFunnelData && (
                             <div className="table-wrapper">
                                <table className="analytics-table">
                                    <thead>
                                        <tr>
                                            <th>Sektion</th>
                                            <th>Besucher</th>
                                            <th>Verbleibend (vom Start)</th>
                                            <th>Absprung (zum Vorgänger)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedFunnelData.map(item => (
                                            <tr key={item.section_name}>
                                                <td>{item.section_name}</td>
                                                <td>{item.unique_visitors}</td>
                                                <td>
                                                    <div className="percentage-bar-container">
                                                        <div className="percentage-bar" style={{width: `${item.retentionPercent}%`}}></div>
                                                        <span className="percentage-bar-label">{item.retentionPercent}%</span>
                                                    </div>
                                                </td>
                                                <td className={item.dropOff > 0 ? 'drop-off-value' : ''}>
                                                    {item.dropOff > 0 ? `-${item.dropOff} (-${item.dropOffPercent}%)` : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}


                {!selectedKeyword && (
                    <div className="notes-section results-section">
                        <h3>Landingpage Änderungsnotizen</h3>
                        <form onSubmit={handleSaveNote} className="note-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="change_date">Datum & Uhrzeit der Änderung:</label>
                                    <input 
                                        type="datetime-local" 
                                        id="change_date"
                                        name="change_date"
                                        value={newNote.change_date}
                                        onChange={handleNewNoteChange}
                                        className="date-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="ad_group">Anzeigengruppe (optional):</label>
                                    <input type="text" id="ad_group" name="ad_group" value={newNote.ad_group} onChange={handleNewNoteChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="location_of_change">Wo geändert (z.B. Headline):</label>
                                    <input type="text" id="location_of_change" name="location_of_change" value={newNote.location_of_change} onChange={handleNewNoteChange} />
                                </div>
                                <div className="form-group form-group-full-width">
                                    <label htmlFor="reason_for_change">Grund der Änderung:</label>
                                    <textarea id="reason_for_change" name="reason_for_change" value={newNote.reason_for_change} onChange={handleNewNoteChange}></textarea>
                                </div>
                                <div className="form-group form-group-full-width">
                                    <label htmlFor="description_of_change">Was wurde geändert? (Beschreibung):</label>
                                    <textarea id="description_of_change" name="description_of_change" value={newNote.description_of_change} onChange={handleNewNoteChange} required></textarea>
                                </div>
                            </div>
                            <button type="submit" className="submit-button note-save-button" disabled={isLoadingNotes}>
                                {isLoadingNotes && newNote.description_of_change ? 'Speichere...' : 'Notiz Speichern'}
                            </button>
                        </form>

                        <h4>Vorhandene Notizen ({notes.length})</h4>
                        {isLoadingNotes && notes.length === 0 && <p className="message-bar loading">Lade Notizen...</p>}
                        {!isLoadingNotes && notes.length === 0 && <p className="message-bar info">Noch keine Notizen vorhanden.</p>}
                        {notes.length > 0 && (
                            <div className="table-wrapper notes-table-wrapper">
                                <table className="analytics-table notes-table">
                                    <thead>
                                        <tr>
                                            <th>Änderungsdatum</th>
                                            <th>Anzeigengruppe</th>
                                            <th>Ort</th>
                                            <th>Grund</th>
                                            <th>Beschreibung</th>
                                            <th>Erfasst am</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notes.map(note => (
                                            <tr key={note.id}>
                                                <td>{formatNoteDateTimeForDisplay(note.change_date)}</td>
                                                <td>{note.ad_group || '-'}</td>
                                                <td>{note.location_of_change || '-'}</td>
                                                <td>{note.reason_for_change || '-'}</td>
                                                <td><pre className="note-description-pre">{note.description_of_change}</pre></td>
                                                <td>{formatNoteDateTimeForDisplay(note.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {!selectedKeyword && !isLoading && (overallStats.totalUniqueUsers > 0 || overallStats.avgOverallDuration !== "N/A") && (
                    <div className="overall-stats-section">
                        <h3>Gesamtüberblick ({startDate.toLocaleDateString('de-DE')} - {endDate.toLocaleDateString('de-DE')})</h3>
                        <div className="stats-cards">
                            <div className="stat-card"><h4>User Gesamt</h4><p>{overallStats.totalUniqueUsers}</p></div>
                            <div className="stat-card"><h4>Ø Aufenthaltsdauer Gesamt</h4><p>{formatDuration(overallStats.avgOverallDuration)}</p></div>
                        </div>
                    </div>
                )}
                
                <div className="charts-grid">
                    {!selectedKeyword && topKeywordsChartData && keywordPerformanceData && keywordPerformanceData.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>Top Keywords</h3>
                            <div className="chart-container" style={{ height: '300px', position: 'relative' }}>
                                <Bar options={keywordChartOptions} data={topKeywordsChartData} />
                            </div>
                        </div>
                    )}
                    {!selectedKeyword && ctaTypeChartData && ctaTypeDistributionData && ctaTypeDistributionData.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>CTA-Typ Verteilung</h3>
                            <div className="chart-container" style={{ height: '300px', position: 'relative' }}>
                                <Pie options={ctaTypeChartOptions} data={ctaTypeChartData} />
                            </div>
                        </div>
                    )}
                    {!selectedKeyword && dailyUserChartData && dailyUserStatsData && dailyUserStatsData.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>Tägliche User</h3>
                            <div className="chart-container" style={{ height: '300px', position: 'relative' }}>
                                <Line options={dailyUserChartOptions} data={dailyUserChartData} />
                            </div>
                        </div>
                    )}

                    {!selectedKeyword && urlUtmSourceChartPreparedData && urlUtmSourceDistribution && urlUtmSourceDistribution.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>Events nach URL UTM Source</h3>
                            <div className="chart-container" style={{ height: '400px', position: 'relative' }}> 
                                <Bar options={urlUtmSourceChartOptions} data={urlUtmSourceChartPreparedData} />
                            </div>
                        </div>
                    )}

                    {!selectedKeyword && urlUtmMediumChartPreparedData && urlUtmMediumDistribution && urlUtmMediumDistribution.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>Events nach URL UTM Medium</h3>
                            <div className="chart-container" style={{ height: '350px', position: 'relative' }}>
                                <Pie options={urlUtmMediumChartOptions} data={urlUtmMediumChartPreparedData} />
                            </div>
                        </div>
                    )}

                    {!selectedKeyword && urlUtmCampaignChartPreparedData && urlUtmCampaignDistribution && urlUtmCampaignDistribution.length > 0 && !isLoading && (
                        <div className="chart-section">
                            <h3>Events nach URL UTM Campaign</h3>
                            <div className="chart-container" style={{ height: '400px', position: 'relative' }}>
                                <Bar options={urlUtmCampaignChartOptions} data={urlUtmCampaignChartPreparedData} />
                            </div>
                        </div>
                    )}
                </div>

                {!selectedKeyword && sortedKeywordPerformanceData && sortedKeywordPerformanceData.length > 0 && (
                    <div className="results-section"> 
                        <h3>Keyword Performance</h3>
                        <div className="table-wrapper">
                            <table className="analytics-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('keyword', 'keyword')} className="sortable-header">Keyword{getSortIndicator('keyword', 'keyword')}</th>
                                        <th onClick={() => requestSort('keyword', 'uniqueUsers')} className="sortable-header">Eindeutige User{getSortIndicator('keyword', 'uniqueUsers')}</th>
                                        <th onClick={() => requestSort('keyword', 'avgDuration')} className="sortable-header">Ø Aufenthaltsdauer{getSortIndicator('keyword', 'avgDuration')}</th>
                                        <th onClick={() => requestSort('keyword', 'sectionsViewed')} className="sortable-header">Abschnitte (Gesamt){getSortIndicator('keyword', 'sectionsViewed')}</th>
                                        <th onClick={() => requestSort('keyword', 'avgSectionsPerUser')} className="sortable-header">Ø Abschnitte / User{getSortIndicator('keyword', 'avgSectionsPerUser')}</th>
                                        <th onClick={() => requestSort('keyword', 'buttonsClicked')} className="sortable-header">Buttons (Gesamt){getSortIndicator('keyword', 'buttonsClicked')}</th>
                                        <th onClick={() => requestSort('keyword', 'avgButtonsPerUser')} className="sortable-header">Ø Buttons / User{getSortIndicator('keyword', 'avgButtonsPerUser')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedKeywordPerformanceData.map((item, index) => (
                                        <tr key={item.keyword + "_" + index} onClick={() => handleKeywordRowClick(item.keyword)} className="clickable-row">
                                            <td>{item.keyword}</td>
                                            <td>{item.uniqueUsers}</td>
                                            <td>{formatDuration(item.avgDuration)}</td>
                                            <td>{item.sectionsViewed}</td>
                                            <td>{typeof item.avgSectionsPerUser === 'number' ? item.avgSectionsPerUser.toFixed(1) : 'N/A'}</td>
                                            <td>{item.buttonsClicked}</td>
                                            <td>{typeof item.avgButtonsPerUser === 'number' ? item.avgButtonsPerUser.toFixed(1) : 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                 {!isLoading && !selectedKeyword && 
                    (!keywordPerformanceData || keywordPerformanceData.length === 0) && 
                    (!ctaTypeDistributionData || ctaTypeDistributionData.length === 0) &&
                    (!dailyUserStatsData || dailyUserStatsData.length === 0) &&
                    (!urlUtmSourceDistribution || urlUtmSourceDistribution.length > 0) && 
                    (!urlUtmMediumDistribution || urlUtmMediumDistribution.length > 0) && 
                    (!urlUtmCampaignDistribution || urlUtmCampaignDistribution.length > 0) && 
                    !message.startsWith("❌") && !message.startsWith("⚠️") && 
                 (
                     <p className="message-bar info">Bitte Zeitraum auswählen und Analyse starten, um Daten anzuzeigen.</p>
                )}

                {selectedKeyword && (
                    <div className="user-detail-section results-section">
                        <h3>User-Details für Keyword: "{selectedKeyword}" 
                            <span className="date-range-display"> ({startDate.toLocaleDateString('de-DE')} - {endDate.toLocaleDateString('de-DE')})</span>
                        </h3>
                        {(isDetailLoading) && <p className="message-bar loading">Lade User-Details...</p>}
                        {!isDetailLoading && sortedKeywordUserDetails && sortedKeywordUserDetails.length > 0 && (
                             <div className="table-wrapper">
                                <table className="analytics-table user-detail-table">
                                    <thead>
                                        <tr>
                                            <th onClick={() => requestSort('userDetail', 'visitorId')} className="sortable-header">Visitor ID{getSortIndicator('userDetail', 'visitorId')}</th>
                                            <th onClick={() => requestSort('userDetail', 'sessionCount')} className="sortable-header">Anz. Sitzungen{getSortIndicator('userDetail', 'sessionCount')}</th>
                                            <th onClick={() => requestSort('userDetail', 'totalDurationFormatted')} className="sortable-header">Gesamtdauer{getSortIndicator('userDetail', 'totalDurationFormatted')}</th>
                                            <th onClick={() => requestSort('userDetail', 'avgDurationPerSessionFormatted')} className="sortable-header">Ø Dauer/Sitzung{getSortIndicator('userDetail', 'avgDurationPerSessionFormatted')}</th>
                                            <th onClick={() => requestSort('userDetail', 'totalSectionsViewed')} className="sortable-header">Abschnitte Gesamt{getSortIndicator('userDetail', 'totalSectionsViewed')}</th>
                                            <th onClick={() => requestSort('userDetail', 'totalButtonsClicked')} className="sortable-header">Buttons Gesamt{getSortIndicator('userDetail', 'totalButtonsClicked')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedKeywordUserDetails.map((detail, index) => (
                                            <tr key={detail.visitorId + "_" + index}>
                                                <td>{detail.visitorId}</td>
                                                <td>{detail.sessionCount}</td>
                                                <td>{formatDuration(detail.totalDurationFormatted)}</td>
                                                <td>{formatDuration(detail.avgDurationPerSessionFormatted)}</td>
                                                <td>{detail.totalSectionsViewed}</td>
                                                <td>{detail.totalButtonsClicked}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {!isDetailLoading && (!sortedKeywordUserDetails || sortedKeywordUserDetails.length === 0) && (
                            <p className="message-bar info">Keine User-Detaildaten für dieses Keyword im ausgewählten Zeitraum gefunden.</p>
                        )}
                    </div>
                )}
                
                <div className="dashboard-footer">
                    <BackButton to="/admin" />
                </div>
            </div>
        </div>
    );
}
